import { ExternalCloseUIElement, HasExternalUIElements, VideoPlayerElements, VideoContext } from "@mp-commons/modules/player/Player.js";
import { DetectedWebView, detectWebView } from "@ciaopeople/cp-shatsu/ClientDetection";
import { BrowserLogger, Logger } from "@ciaopeople/cp-shatsu/Logging";
import { SimpleEventHandler } from "@ciaopeople/cp-shatsu/EventHandler";
import { MegaridePlayerControls, MegaridePlayerGUI } from "../../MegaridePlayer.js";
import { ParthenopePlayerConfig } from "../../MegaridePlayerConfig.js";
import "./v2/style/style.scss";
import { AdContainerControls } from "@mp-commons/modules/ads/AdsController.js";
import { MainComponent } from "./v2/components/MainComponent.js";
import { isFunction } from "@ciaopeople/cp-shatsu/TypeChecking";
import { MediaState } from "@mp-commons/modules/player/AbstractPlayerControls.js";
import { GenericFormatLabel } from "@mp-commons/modules/player/video/FormatAndSizePreference.js";

export const PARTHENOPE_GLOBAL_CSS_PREFIX = "ymp";

/**
 * Implements the graphical user interface (GUI) for the Parthenope video player.
 * 
 * `ParthenopeGUIV2` manages the main player UI, fullscreen handling, wake lock state,
 * video context, event listeners, and external UI elements. It coordinates with
 * player controls and the main component to provide playback, navigation, and
 * error handling functionalities.
 * 
 * ### Features
 * - Fullscreen management with cross-browser support
 * - Wake lock acquisition and release for uninterrupted playback
 * - Responsive resizing via `ResizeObserver`
 * - Integration with external UI elements (e.g., close button)
 * - Poster image retrieval and initialization
 * - Handles navigation (next, previous, restart) and playlist looping
 * - Error reporting and casting stubs
 * - Anchoring and loading state updates
 * 
 * ### Constructor
 * @param window - The browser window object
 * @param rootElement - The root HTML element for the player UI (defaults to `document.body`)
 * @param config - Player configuration options
 * 
 * ### Implements
 * - `MegaridePlayerGUI`
 * - `HasExternalUIElements`
 * 
 * @public
 */
export class ParthenopeGUIV2 implements MegaridePlayerGUI, HasExternalUIElements {

    
    private logger: Logger;
    private controls: MegaridePlayerControls;
    vwDetect: DetectedWebView;
    private mainComponent: MainComponent;
    private rootElement: HTMLElement;
    private videoContext: VideoContext;
    private isFullscreen: boolean;
    private resizeObserver: ResizeObserver;
    internalEvents: SimpleEventHandler;
    private wakeLock: WakeLockSentinel;
    private resourceEnded: boolean;
    private steady: boolean;
    
    
    private set mediaState(ms: MediaState) {
        this._mediaState = ms;
    }

    public get mediaState(): MediaState {
        return this._mediaState;
    }
    
    private _mediaState: MediaState;
    
    private evtListeners: {
        ended: EventListener
    }

    get rootEl(): HTMLElement {
        return this.rootElement
    }

    get fullscreen(): boolean {
        return this.isFullscreen;
    }

    get generalMute(): boolean {
        return this.controls?.getGeneralMute();
    }

    get restartEnabled(): boolean {
        return !this.forwardNavigationEnabled && this.resourceEnded
    }

    private lockWakeState(): void {
        const canWakeLock = 'wakeLock' in this.window.navigator;

        if (!canWakeLock) return;

        try {
            // trying to acquire wakelock sentinel
            this.window.navigator.wakeLock.request("screen").then(wakeLock => {
                // wakelock sentinel acquired
                this.wakeLock = wakeLock;
                // listener on release of lock wake state
                this.wakeLock.addEventListener('release', () => {
                    this.logger.log('Screen Wake State Locked:', !this.wakeLock.released);
                    if (this.wakeLock.released)
                        this.wakeLock = null;
                });
                this.logger.log('Screen Wake State Locked:', !this.wakeLock.released);

            }).catch(err => {
                this.logger.error('Failed to acquire lock wake sentinel with reason:', err.message);
                this.releaseWakeLock();
            })
        } catch (e) {
            this.logger.error('Failed to lock wake state with reason:', e.message);
            this.releaseWakeLock();
        }

    }

    private releaseWakeLock(): void {
        if (this.wakeLock) {
            this.wakeLock.release();
        }
    }

    get headEl(): HTMLElement {
        return this.mainComponent.headEl;
    }

    constructor(public readonly window: Window,
        rootElement: HTMLElement = null,
        private readonly config: ParthenopePlayerConfig) {

        this.wakeLock = null;
        this.evtListeners = {
            ended: null
        }

        this.internalEvents = new SimpleEventHandler();

        this.logger = new BrowserLogger("ParthenopeGUIV2", "#fed21f");
        this.rootElement = rootElement ? rootElement : window.document.body;
        
        this.resizeObserver = new ResizeObserver((entries) => {
            entries.forEach(entry => {
                this.internalEvents.trigger("player_resize", {});
            });
        });
        
        this.resizeObserver.observe(this.rootElement);
        
        this.vwDetect = detectWebView();
        this.videoContext = null;
        //se si vuole riutilizzare un elemento video presente in pagina
        let maybeVideo: HTMLVideoElement = null;
        if (this.config.reuseExistingVideoElement) {
            maybeVideo = this.rootElement.querySelector("video");
            if (maybeVideo) this.rootElement.removeChild(maybeVideo);
        }
        else {
            this.rootElement.childNodes.forEach(x => x.remove());
        }

        this.mainComponent = new MainComponent(this,
            {
                titleBar: config.embeddablePlayerConfig?.bar || false,
                titlePlaceholder: config.embeddablePlayerConfig?.titlePlaceholder,
                initialVolume: config.embeddablePlayerConfig?.volume || 50,
                enableNavigationPlaylist: false,
                initialPoster: this.retrieveInitialPoster(config),
                product: config.embeddablePlayerConfig?.css || config.domain.network,
                onlyVideoPoster: config.onlyVideoPoster,
                enableStoryboard: config.enableStoryboard
            },
            maybeVideo);

        this.rootElement.appendChild(this.mainComponent.html);


        this.isFullscreen = false;

        this.window.addEventListener('keydown', (e) => {
            if (e.key === 'Escape' && this.fullscreen) {
                this.toggleFullscreen();
            }
        })

        this.attachFullScreenListeners()

    }
    

    private retrieveInitialPoster(config: ParthenopePlayerConfig): string {
        let usePosterUrl = config.initialPoster;

        if (config.embeddablePlayerConfig.thumbPlaceholder) {
            const thumbPlh = this.window.document.querySelector(`#${config.embeddablePlayerConfig.thumbPlaceholder}`);
            if (thumbPlh) {
                let useThumbPlhSource = undefined;
                if (this.vwDetect.device?.other && thumbPlh.querySelector("source")?.srcset) {
                    useThumbPlhSource = thumbPlh.querySelector("source")?.srcset;
                } else if (thumbPlh.querySelector("img")?.src) {
                    useThumbPlhSource = thumbPlh.querySelector("img")?.src;
                }
                if (useThumbPlhSource) {
                    usePosterUrl = useThumbPlhSource;
                }
                thumbPlh.remove();
            }
        }
        return usePosterUrl;
    }

    get playerEl() {
        return this.mainComponent.html;
    }

    private get fullScreenEl() {
        return this.vwDetect.platform.ios ? this.getVideoElement().mediaItem : this.mainComponent.html;

    }
    getAdContainer(): AdContainerControls {
        return this.mainComponent.getAdContainer();
    }

    setVideoContext(ctx: VideoContext) {

        this.resourceEnded = false;
        const endListener = (evt) => {
            this.resourceEnded = true;
            if(!this.config.embeddablePlayerConfig.manualPlaylist) {
                if(this.forwardNavigationEnabled)
                    this.playNextVideo(false)
                else if(this.config.embeddablePlayerConfig.loopPlaylist) {
                    this.restart(false)
                }
            }
        }

        ctx.on("ended", endListener)

        if (this.evtListeners?.ended) this.videoContext.off("ended", this.evtListeners.ended)
        this.evtListeners.ended = endListener

        this.videoContext = ctx;
        this.mainComponent.setVideoContext(ctx);
    }

    getVideoElement(): VideoPlayerElements {
        return this.mainComponent.getPlayerVideoElement();
    }

    setPlayerControls(controls: MegaridePlayerControls): void {
        this.controls = controls;
        this.mediaState = this.controls.currentMediaState;
        this.controls.subscribeMediaStatus((s: MediaState) => {
            this.mediaState = s;
        })
    }

    showError(err: string): void {
        alert(err);
    }

    getPlayerMediaElementsForId(videoId: string): VideoPlayerElements {
        return this.getVideoElement();
    }


    playNextVideo(forceResume: boolean): void {
        this.controls.playNext(forceResume)
    }

    playPreviousVideo(forceResume: boolean): void {
        this.controls.playPrevious(forceResume)
    }

    restart(forceResume: boolean): void {
        this.controls.restart(forceResume)
    }

    toggleCast(evt: Event): void {
        console.warn("Cast not supported at the moment")
    }

    updateCastStatus(enabled: boolean): void {
        console.warn("Cast not supported at the moment")
    }

    get forwardNavigationEnabled(): boolean {
        return this.controls.hasNext()
    }

    get backNavigationEnabled(): boolean {
        return this.controls.hasPrevious()
    }

   

    setFullscreen(fullscreen: boolean): void {
        const element = this.fullScreenEl;
        const requestFullScreen = element.requestFullscreen || element['mozRequestFullScreen'] || element['webkitRequestFullScreen'] || element['msRequestFullscreen'] || element['webkitEnterFullscreen'];
        const cancelFullScreen = this.window.document.exitFullscreen || this.window.document['mozCancelFullScreen'] || this.window.document['webkitExitFullscreen'] || this.window.document['msExitFullscreen'] || this.window.document['webkitExitFullscreen'];

        if(fullscreen) {
            if (requestFullScreen && isFunction(requestFullScreen)) {
                try {
                    requestFullScreen.bind(element)()
                } catch (error) {
                    this.logger.error("Error webkit fullscreen, try calling resource fullscreen handler", error)
                    this.controls.getCurrentPlayingMediaContext().toggleFullscreen(true)
                }
            }
        } else {
            if (cancelFullScreen && isFunction(cancelFullScreen)) {
                
                try {
                    cancelFullScreen.bind(document)()
                } catch (error) {
                    this.logger.error("Error webkit fullscreen, try calling resource fullscreen handler", error)
                    this.controls.getCurrentPlayingMediaContext().toggleFullscreen(false)
                }
            }
        }
    }

    // funzione che viene richiamata per cambiare lo stato di fullscreen
    toggleFullscreen(): void {
        this.setFullscreen(!this.isFullscreen);
    }

    private attachFullScreenListeners() {

        const handleFullScreenChange = (e) => {
            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation();
            this.isFullscreen = this.window.document.fullscreenElement != null || this.window.document['webkitFullscreenElement'] != null || this.window.document['mozFullScreenElement'] != null;

            const isHorizontal = this.videoContext?.getCurrentFormat()?.parsedSize?.aspectRatio.isHorizontal() || false;

            if (this.isFullscreen) {
                this.lockWakeState();
                if(isHorizontal)
                    this.window.screen.orientation.lock("landscape");

                this.controls.getCurrentVideoContext().then(ctx => {
                    if(ctx.getCurrentFormat().label != GenericFormatLabel.p720)
                        ctx.switchQuality(GenericFormatLabel.p720)
                })
            } else {
                this.releaseWakeLock();
                if(isHorizontal)
                    this.window.screen.orientation.unlock();
            }
            this.mainComponent.fullscreenChanged(this.isFullscreen).then(_ => {
                setTimeout(() => {
                    this.internalEvents.trigger("player_resize", {});
                }, 500);
            })
        }

        this.fullScreenEl.addEventListener('webkitfullscreenchange', handleFullScreenChange);
        this.fullScreenEl.addEventListener('mozfullscreenchange', handleFullScreenChange);
        this.fullScreenEl.addEventListener('fullscreenchange', handleFullScreenChange);
        this.fullScreenEl.addEventListener('webkitbeginfullscreen', handleFullScreenChange, false);
        this.fullScreenEl.addEventListener('webkitendfullscreen', handleFullScreenChange, false);
    }

    anchorageUpdate(anchored: boolean) {
        this.mainComponent.anchorageUpdate(anchored)
    }

    loadingUpdate(loading: boolean): void {
        this.mainComponent.loadingUpdate(loading)
    }

    private _externalCloseUIElement?: ExternalCloseUIElement;

    getCloseExternalUIElement(): ExternalCloseUIElement {
        return this._externalCloseUIElement;
    }

    setExternalCloseUIElement(el: ExternalCloseUIElement): void {
        this._externalCloseUIElement = el;
    }


}
